import _regeneratorRuntime from '@babel/runtime/helpers/esm/regeneratorRuntime';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import { throttleUntilResolve, jwtDecode, isTokenExpired } from '@dtvgo/utils';

/**
 * Creates a function to get the updated authorization headers.
 * @param {Object} options - The options object.
 * @param {AuthHeadersData} [options.initialData] - The initial data.
 * @param {Authorizer} options.authorizer - The authorizer object.
 * @param {(error: Error) => void} options.onError - The error handler function.
 * @param {number} [options.refreshSecondsBefore] - The number of seconds to refresh the access
 * token before expiration.
 * @returns {{
 *   getAuthHeaders: import('../utils').GetHeadersCallback,
 *   clearRefreshTimeout: () => void
 * }}
 */
function getAuthHeadersGetter(_ref) {
  var initialData = _ref.initialData,
    authorizer = _ref.authorizer,
    onError = _ref.onError,
    _ref$refreshSecondsBe = _ref.refreshSecondsBefore,
    secondsBefore = _ref$refreshSecondsBe === void 0 ? 90 : _ref$refreshSecondsBe;
  /** @type {AuthHeadersData} */
  var data = {
    accessToken: undefined,
    refreshToken: undefined,
    issuedAt: undefined
  };
  var refreshTokenTimeout;
  var getToken = throttleUntilResolve( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    var response;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.next = 2;
          return authorizer.getToken(data);
        case 2:
          response = _context.sent;
          updateData(response.data);
        case 4:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })));
  var refreshAccessToken = throttleUntilResolve( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    var response;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _context2.next = 2;
          return authorizer.refreshToken(data);
        case 2:
          response = _context2.sent;
          updateData(response.data);
        case 4:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  })));

  /**
   * Updates the data with new access and refresh tokens.
   * @param {AuthHeadersData} updatedData
   */
  function updateData(_ref4) {
    var accessToken = _ref4.accessToken,
      refreshToken = _ref4.refreshToken,
      issuedAt = _ref4.issuedAt;
    data.accessToken = accessToken;
    data.refreshToken = refreshToken;
    data.issuedAt = issuedAt || Date.now();
    var _jwtDecode = jwtDecode(accessToken),
      exp = _jwtDecode.exp,
      iat = _jwtDecode.iat;
    var expirationTime = exp - iat;
    if (refreshTokenTimeout) clearTimeout(refreshTokenTimeout);
    refreshTokenTimeout = setTimeout(function () {
      refreshAccessToken()["catch"](onError);
      refreshTokenTimeout = undefined;
    }, (expirationTime - secondsBefore) * 1000);
  }
  var getUpdatedAccessToken = /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3() {
      var isValidAccessToken, isValidRefreshToken;
      return _regeneratorRuntime().wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            isValidAccessToken = !!data.accessToken && !isTokenExpired(data.accessToken, {
              secondsBefore: secondsBefore,
              issuedAt: data.issuedAt
            });
            if (!isValidAccessToken) {
              _context3.next = 3;
              break;
            }
            return _context3.abrupt("return", data.accessToken);
          case 3:
            isValidRefreshToken = !!data.refreshToken && !isTokenExpired(data.refreshToken, {
              issuedAt: data.issuedAt
            });
            if (!isValidRefreshToken) {
              _context3.next = 8;
              break;
            }
            _context3.next = 7;
            return refreshAccessToken();
          case 7:
            return _context3.abrupt("return", data.accessToken);
          case 8:
            _context3.next = 10;
            return getToken();
          case 10:
            return _context3.abrupt("return", data.accessToken);
          case 11:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }));
    return function getUpdatedAccessToken() {
      return _ref5.apply(this, arguments);
    };
  }();
  if (initialData) updateData(initialData);
  if (!data.accessToken) getToken()["catch"](onError);

  /**
   * Get the authentication headers for the request.
   */
  var getAuthHeaders = /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4() {
      return _regeneratorRuntime().wrap(function _callee4$(_context4) {
        while (1) switch (_context4.prev = _context4.next) {
          case 0:
            _context4.prev = 0;
            _context4.t0 = "Bearer ";
            _context4.next = 4;
            return getUpdatedAccessToken();
          case 4:
            _context4.t1 = _context4.sent;
            _context4.t2 = _context4.t0.concat.call(_context4.t0, _context4.t1);
            return _context4.abrupt("return", {
              Authorization: _context4.t2
            });
          case 9:
            _context4.prev = 9;
            _context4.t3 = _context4["catch"](0);
            console.error('[player-utils] getAuthHeaders error', _context4.t3);
            onError(_context4.t3);
            return _context4.abrupt("return", {});
          case 14:
          case "end":
            return _context4.stop();
        }
      }, _callee4, null, [[0, 9]]);
    }));
    return function getAuthHeaders() {
      return _ref6.apply(this, arguments);
    };
  }();

  /**
   * Clears the refresh token timeout.
   */
  var clearRefreshTimeout = function clearRefreshTimeout() {
    clearTimeout(refreshTokenTimeout);
  };
  return {
    getAuthHeaders: getAuthHeaders,
    clearRefreshTimeout: clearRefreshTimeout
  };
}

/**
 * @typedef {object} AuthHeadersData
 * @property {string} accessToken - The access token.
 * @property {string} refreshToken - The refresh token.
 * @property {number} [issuedAt] - The issued at timestamp.
 */

/**
 * @callback GetOrRefreshTokenCallback
 * @param {AuthHeadersData} authHeadersData - The current auth headers data.
 * @returns {Promise<{ data: AuthHeadersData }>} - The new auth headers data.
 */

/**
 * @typedef {object} Authorizer
 * @property {GetOrRefreshTokenCallback} getToken - Function to get the token
 * @property {GetOrRefreshTokenCallback} refreshToken - FUnction to refresh the token
 */

export { getAuthHeadersGetter };
//# sourceMappingURL=getAuthHeadersGetter.js.map
